(ns common.widgets.pan-zoom
  "Element panning with @dnd-kit

  The components are implemented as raw JS React components,
  no Reagent. This seems simpler, as some elements need to
  use JS objects for properties and merge them anyway."
  (:require ["@dnd-kit/core" :refer [useDraggable DndContext]]
            ["@dnd-kit/utilities" :refer [CSS]]
            [common.util :refer [spread-props]]
            [uix.core :as uix :refer [defui $ defhook]]))

;; Checker background for PanArea
(def checker-png "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAAAXNSR0IArs4c6QAAAONJREFUeF7t20EOhEAIRFG4/6F7DvEnYeFzryQIv6pBd2behOu9dPvsbog+k+NLgArQAqmJcw9iAAhSgZKB3IJkkAySQTJ4CiE+gA8oBeg0mH3Ai084P89HhqwEqIA209ICsQdjAeaZIgaAYKxBDMCAYy8fXwAIgiAIcoJpJEYGI4VjB3YrbC9gL2AvkCB43cM5PgZgAAZgQFnNZAhdGykQBEEQBEEQDBmgAm2glM/z+QUYisYUGoldO7kY32IEAzCg6RgIRgjFAsw+AgRBMNYgBmCAT2TCYfoPPz/HCqQCX1eBHzHnv7C7WhBSAAAAAElFTkSuQmCC)")

(defhook use-zoom-control [{:keys [zoom]}]
  (let [ref (uix/use-ref)

        ;; Attach scroll handler directly because React attaches the
        ;; event handlers into document and forwards them to React elements.
        ;; We need to attach the wheel event handler to the element, so
        ;; preventDefault works correctly to disable parent element/document scroll.
        scroll-handler (uix/use-callback (fn [e]
                                           (.preventDefault e)
                                           (zoom (> (.-deltaY e) 0)))
                                         [zoom])]
    (uix/use-effect (fn []
                      (when-let [el @ref]
                        (.addEventListener el "wheel" scroll-handler #js {:passive false})
                        (fn []
                          (.removeEventListener el "wheel" scroll-handler #js {:passive false}))))
                    [@ref scroll-handler])
    ref))

(defui pan-area
  "When pan (dragging) ends, calls set-pos => (current x,y) => x,y"
  [{:keys [set-pos style children]}]
  ($ DndContext
     {:onDragEnd (fn [^js e]
                   (set-pos (fn [[x y]]
                              [(+ x (.-x (.-delta e)))
                               (+ y (.-y (.-delta e)))])))}
     ($ :div
        {:style (merge {:overflow "hidden"
                        :position "relative"}
                       style)}
        children)))

(defui checker [_]
  ($ :div
     {:style {:position "absolute"
              :top 0
              :left 0
              :right 0
              :bottom 0
              :backgroundImage checker-png
              :backgroundImageRepeat "repeat"
              :opacity 0.04}}))

(defui pan-item
  [{:keys [children scale style]}]
  (let [draggable (useDraggable #js {:id "template-preview-pan"})
        transform (.. CSS -Transform (toString (js/Object.assign
                                                #js {}
                                                (.-transform draggable)
                                                #js {:scaleX scale
                                                     :scaleY scale})))]
    ($ :div
       (spread-props {:ref (.-setNodeRef draggable)
                      :style (merge {:position "relative"
                                     :transform transform}
                                    style)}
                     (.-listeners draggable)
                     (.-attributes draggable))
       children)))

(defn centered-item-style
  "Places the PanItem into PanArea so that it is by default centered.

  Needs the current position (x,y) and unscaled dimensions (w,h) for the element."
  [x y w h]
  {;; Add 50% of container size to center the element.
   ;; Add the saved pan position.
   ;; If x,y is 0,0, the PanItem is centered.
   :left (str "calc(50% + " x "px)")
   :top (str "calc(50% + " y "px)")
   ;; Remove half of the element size to center the element to the element anchor.
   :margin-left (when w (- (/ w 2)))
   :margin-top (when h (- (/ h 2)))
   ;; Set definitive size for the element, same as the iframe
   :width w
   :height h})

(defn clamp [v min-v max-v]
  (min (max v min-v) max-v))

(defn update-zoom
  [set-scale zoom-factor min-zoom max-zoom down]
  (set-scale (fn [v]
               (clamp (* v (if down (/ 1 zoom-factor) zoom-factor)) min-zoom max-zoom))))

(defn limited-set-pos
  "Resulting new x,y value is limited to within the available area.
  0,0 is center of the area, so x range is -0.5w to 0.5w etc."
  [set-pos w h f]
  (set-pos (fn [current]
             (let [[x y] (f current)
                   max-x (/ w 2)
                   max-y (/ h 2)]
               [(clamp x (- max-x) max-x)
                (clamp y (- max-y) max-y)]))))
